// API key management (PRD §4.11). Keys are minted server-side; the plaintext is
// returned exactly once (on create) and never retrievable again — the stored doc
// holds only a prefix + last4 for display. Reads come straight from
// tenants/{t}/apiKeys (members can read; no secret in the doc); create/revoke go
// through owner/admin-guarded callables.
import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { useTenant } from './tenant';

export interface ApiKey {
  id: string;
  label: string;
  prefix: string;
  last4: string;
  createdAt: number;
  lastUsedAt: number | null;
  revoked: boolean;
}

export function useApiKeys(): { keys: ApiKey[]; loading: boolean } {
  const { tenantId } = useTenant();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setKeys([]); setLoading(false); return; }
    const q = query(collection(db, `tenants/${tenantId}/apiKeys`), orderBy('createdAt', 'desc'));
    return onSnapshot(
      q,
      (snap) => {
        setKeys(snap.docs.map((d) => {
          const k = d.data() as Partial<ApiKey>;
          return {
            id: d.id,
            label: k.label ?? '',
            prefix: k.prefix ?? '',
            last4: k.last4 ?? '',
            createdAt: k.createdAt ?? 0,
            lastUsedAt: k.lastUsedAt ?? null,
            revoked: Boolean(k.revoked),
          };
        }));
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [tenantId]);

  return { keys, loading };
}

interface CreateResult { id: string; key: string; label: string; prefix: string; last4: string; }
const createFn = httpsCallable<{ label: string }, CreateResult>(functions, 'apiKeyCreate');
const revokeFn = httpsCallable<{ id: string }, { ok: boolean }>(functions, 'apiKeyRevoke');

/** Create a key. The returned `key` is the ONLY time the plaintext is available. */
export function createApiKey(label: string): Promise<CreateResult> {
  return createFn({ label }).then((r) => r.data);
}

export function revokeApiKey(id: string): Promise<{ ok: boolean }> {
  return revokeFn({ id }).then((r) => r.data);
}
